package fyi.moko.hotpl.domain.usecase

import fyi.moko.hotpl.domain.model.Place
import fyi.moko.hotpl.domain.model.normalizeForComparison
import fyi.moko.hotpl.domain.model.normalizePlaceNameForStorage
import fyi.moko.hotpl.domain.model.normalizeTags
import fyi.moko.hotpl.domain.repository.PlaceRepository

/** Creates or updates a place. Returns the id of the saved place. */
sealed interface SavePlaceResult {
    data class Saved(val id: String) : SavePlaceResult
    data class Rejected(val reason: SavePlaceRejection) : SavePlaceResult
    data object DuplicateNameWarning : SavePlaceResult
}

enum class SavePlaceRejection {
    EmptyName,
}

class SavePlaceUseCase(
    private val repository: PlaceRepository,
) {
    /**
     * [firstVisitAt]: 새 장소의 암묵적 첫 방문에 찍을 시각. 사진 EXIF로 "언제 갔는지"를
     * 알아낸 경우에만 들어오고, 없으면 저장소가 "지금"으로 찍는다.
     */
    suspend operator fun invoke(
        place: Place,
        saveAnyway: Boolean = false,
        firstVisitAt: Long? = null,
    ): SavePlaceResult {
        val normalizedName = normalizeForComparison(place.name)
        if (normalizedName.isBlank()) {
            return SavePlaceResult.Rejected(SavePlaceRejection.EmptyName)
        }

        if (!saveAnyway && repository.hasPlaceWithNormalizedName(
                normalizedName = normalizedName,
                excludePlaceId = place.id.takeIf { it.isNotBlank() },
            )
        ) {
            return SavePlaceResult.DuplicateNameWarning
        }

        val sanitizedPlace = place.copy(
            name = normalizePlaceNameForStorage(place.name),
            note = place.note?.trim()?.ifBlank { null },
            locationContext = place.locationContext?.trim()?.ifBlank { null },
            tags = normalizeTags(place.tags),
        )
        return SavePlaceResult.Saved(repository.savePlace(sanitizedPlace, firstVisitAt))
    }
}
